import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Select, MenuItem, FormControl, InputLabel, Chip, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, CircularProgress, Fade, Slide, Snackbar, Alert, Collapse, Divider
} from '@mui/material';
import { Search, ExpandMore, ExpandLess, Edit, Delete, Save, Cancel, TrendingUp, TrendingDown, TrendingFlat, SmartToy, Close } from '@mui/icons-material';
import { TenantConfig } from '../tenant-config';
import ChatInterface from './ChatInterface';

interface SimpleReportProps {
  tenantConfig: TenantConfig;
}

interface Transaction {
  date: string;
  title: string;
  category: string;
  type: 'income' | 'expense';
  amount: number;
  rowIndex: number; // Row index in Google Sheets for edit/delete
}

const SimpleReport: React.FC<SimpleReportProps> = ({ tenantConfig }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showMonthlyReports, setShowMonthlyReports] = useState(false);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [showAmounts, setShowAmounts] = useState(true);
  const [excludeKeywords, setExcludeKeywords] = useState<string[]>(() => {
    const saved = localStorage.getItem('excludeKeywords');
    return saved ? JSON.parse(saved) : ['lương', 'thu nhập', 'salary', 'income'];
  });


  // Filter states
  const [searchText, setSearchText] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedType, setSelectedType] = useState('');

  // Edit/Delete states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [editFormData, setEditFormData] = useState({
    date: '',
    title: '',
    category: '',
    type: 'expense' as 'income' | 'expense',
    amount: 0
  });

  // Loading and feedback states
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');


  const ITEMS_PER_PAGE = 10;
  const LARGE_AMOUNT_THRESHOLD = 10000000000; // 1 tỷ


  const fetchTransactions = useCallback(async () => {
    try {
      setIsLoading(true);
      const range = `${tenantConfig.sheetName}!A:F`;
      const url = `https://sheets.googleapis.com/v4/spreadsheets/1LbMSWz41ukFdbAZFv1q33XgDqwZKFEixBujI77fbwko/values/${range}?key=${tenantConfig.googleApiKey}`;

      const response = await fetch(url);
      const data = await response.json();

      if (data.values && data.values.length > 1) {
        const parsedTransactions: Transaction[] = data.values.slice(1).map((row: any[], index: number) => ({
          date: row[0] || '',
          title: row[1] || '',
          category: row[2] || '',
          type: (row[3] || '').toLowerCase() === 'income' ? 'income' : 'expense',
          amount: parseFloat(row[4]) || 0,
          rowIndex: index + 2 // +2 because we skip header row and Google Sheets is 1-indexed
        })).filter((t: Transaction) => t.date && t.title);

        parsedTransactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        setTransactions(parsedTransactions);
      } else {
        setTransactions([]);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Không thể tải dữ liệu từ Google Sheets');
    } finally {
      setIsLoading(false);
    }
  }, [tenantConfig.sheetName, tenantConfig.googleApiKey]);

  const applyFilters = useCallback(() => {
    let filtered = [...transactions];

    if (searchText) {
      filtered = filtered.filter((t: Transaction) =>
        t.title.toLowerCase().includes(searchText.toLowerCase()) ||
        t.category.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (startDate) {
      filtered = filtered.filter((t: Transaction) => t.date >= startDate);
    }

    if (endDate) {
      filtered = filtered.filter((t: Transaction) => t.date <= endDate);
    }

    if (selectedCategory) {
      filtered = filtered.filter((t: Transaction) => t.category === selectedCategory);
    }

    if (selectedType) {
      filtered = filtered.filter((t: Transaction) => t.type === selectedType);
    }

    setFilteredTransactions(filtered);
    setPage(1);
    setHasMore(filtered.length > ITEMS_PER_PAGE);
  }, [transactions, searchText, startDate, endDate, selectedCategory, selectedType]);

  const clearFilters = () => {
    setSearchText('');
    setStartDate('');
    setEndDate('');
    setSelectedCategory('');
    setSelectedType('');
  };

  // Edit/Delete handlers
  const handleEditClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setEditFormData({
      date: transaction.date,
      title: transaction.title,
      category: transaction.category,
      type: transaction.type,
      amount: transaction.amount
    });
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setDeleteDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!selectedTransaction) return;

    setIsEditing(true);
    try {
      const response = await fetch(tenantConfig.serverApiUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        body: JSON.stringify({
          sheetName: tenantConfig.sheetName,
          rowIndex: selectedTransaction.rowIndex,
          transaction: editFormData
        }),
      });

      if (response.ok) {
        setEditDialogOpen(false);
        setSelectedTransaction(null);
        setSnackbarMessage('Giao dịch đã được cập nhật thành công!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        await fetchTransactions(); // Refresh data
      } else {
        const errorData = await response.json();
        setSnackbarMessage(`Không thể cập nhật giao dịch: ${errorData.error || 'Unknown error'}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error('Error updating transaction:', err);
      setSnackbarMessage('Lỗi khi cập nhật giao dịch');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsEditing(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTransaction) return;

    setIsDeleting(true);
    try {
      const response = await fetch(tenantConfig.serverApiUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        body: JSON.stringify({
          sheetName: tenantConfig.sheetName,
          rowIndex: selectedTransaction.rowIndex
        }),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Delete success:', result);
        setDeleteDialogOpen(false);
        setSelectedTransaction(null);
        setSnackbarMessage('Giao dịch đã được xóa thành công!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        // Force refresh data
        await fetchTransactions();
      } else {
        const errorData = await response.json();
        console.error('Delete failed:', errorData);
        setSnackbarMessage(`Không thể xóa giao dịch: ${errorData.error || 'Unknown error'}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error('Error deleting transaction:', err);
      setSnackbarMessage('Lỗi khi xóa giao dịch');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsDeleting(false);
    }
  };


  // useEffect hooks
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);


  const loadMore = () => {
    setPage(prev => prev + 1);
  };

  // Lấy dữ liệu tháng hiện tại
  const getCurrentMonthData = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    return transactions.filter((t: Transaction) => {
      const transactionDate = new Date(t.date);
      return transactionDate.getFullYear() === currentYear &&
        transactionDate.getMonth() + 1 === currentMonth;
    });
  };

  // Lấy dữ liệu 3 tháng gần nhất
  const getLast3MonthsData = () => {
    const now = new Date();
    const months = [];

    for (let i = 0; i < 3; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;

      const monthData = transactions.filter((t: Transaction) => {
        const transactionDate = new Date(t.date);
        return transactionDate.getFullYear() === year &&
          transactionDate.getMonth() + 1 === month;
      });

      months.push({
        year,
        month,
        label: `${month}/${year}`,
        income: monthData.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0),
        expense: monthData.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0)
      });
    }

    return months.reverse();
  };

  // Lấy dữ liệu 3 ngày gần nhất + ngày này tháng trước
  const getLast3DaysData = () => {
    const now = new Date();
    const days = [];

    // Add last 3 days
    for (let i = 2; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];

      const dayData = transactions.filter((t: Transaction) => t.date === dateStr);

      const expense = dayData.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);

      days.push({
        date: dateStr,
        label: i === 0 ? 'Hôm nay' : i === 1 ? 'Hôm qua' : `${date.getDate()}/${date.getMonth() + 1}`,
        expense
      });
    }

    // Add same day last month
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    const lastMonthDateStr = lastMonth.toISOString().split('T')[0];
    const lastMonthData = transactions.filter((t: Transaction) => t.date === lastMonthDateStr);
    const lastMonthExpense = lastMonthData.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);

    days.push({
      date: lastMonthDateStr,
      label: `Tháng trước (${lastMonth.getDate()}/${lastMonth.getMonth() + 1})`,
      expense: lastMonthExpense
    });

    return days;
  };


  // Phân tích category
  const getCategoryAnalysis = () => {
    const currentMonthData = getCurrentMonthData();
    const categoryMap = new Map<string, number>();

    currentMonthData.forEach((t: Transaction) => {
      if (t.type === 'expense') {
        // Check if category contains any exclude keywords
        const categoryLower = t.category.toLowerCase();
        const shouldExclude = excludeKeywords.some(keyword =>
          categoryLower.includes(keyword.toLowerCase())
        );

        if (!shouldExclude) {
          const current = categoryMap.get(t.category) || 0;
          categoryMap.set(t.category, current + t.amount);
        }
      }
    });

    return Array.from(categoryMap.entries())
      .map(([category, amount]) => ({ category, amount }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5); // Top 5 categories
  };

  // Add category to exclude list
  const handleExcludeCategory = (category: string) => {
    const newExcludeList = [...excludeKeywords, category];
    setExcludeKeywords(newExcludeList);
    localStorage.setItem('excludeKeywords', JSON.stringify(newExcludeList));
    setSnackbarMessage(`Đã loại bỏ "${category}" khỏi Top 5 Chi Tiêu`);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  // Tính tổng theo filtered transactions (có thể là tất cả hoặc theo filter)
  const totalIncome = filteredTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
  const totalExpense = filteredTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);
  const balance = totalIncome - totalExpense;

  const displayedTransactions = filteredTransactions.slice(0, page * ITEMS_PER_PAGE);
  const canLoadMore = hasMore && displayedTransactions.length < filteredTransactions.length;

  const formatMoney = (amount: number) => {
    return amount.toLocaleString('vi-VN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const formatMoneyShort = (amount: number) => {
    if (amount >= 1000000000) {
      return `${(amount / 1000000000).toFixed(1)}B`;
    } else if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(0)}k`;
    }
    return amount.toString();
  };

  const shouldHideAmount = (amount: number) => {
    return amount > LARGE_AMOUNT_THRESHOLD;
  };

  const renderAmount = (amount: number, type: 'income' | 'expense') => {
    if (shouldHideAmount(amount)) {
      return (
        <Typography variant="body1" fontWeight="bold" color={type === 'income' ? 'success.main' : 'error.main'}>
          ***
        </Typography>
      );
    }

    return (
      <Typography variant="body1" fontWeight="bold" color={type === 'income' ? 'success.main' : 'error.main'}>
        {type === 'income' ? '+' : '-'}{formatMoney(amount)}đ
      </Typography>
    );
  };

  const renderSummaryAmount = (amount: number) => {
    // if (shouldHideAmount(amount)) {
    //   return (
    //     <Typography variant="h6">***</Typography>
    //   );
    // }

    return (
      <Typography variant="h6">
        {formatMoney(amount)}
      </Typography>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 10 }}>
      {/* Summary Cards - Chỉ hiển thị khi có filter */}
      {(searchText || startDate || endDate || selectedCategory || selectedType) && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 120 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)', color: 'white' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                {renderSummaryAmount(totalIncome)}
                <Typography variant="body2">
                  Tổng Thu (Đã lọc)
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: 1, minWidth: 120 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)', color: 'white' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                {renderSummaryAmount(totalExpense)}
                <Typography variant="body2">
                  Tổng Chi (Đã lọc)
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: 1, minWidth: 120 }}>
            <Card sx={{ background: balance >= 0 ? 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)' : 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)', color: 'white' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                {renderSummaryAmount(balance)}
                <Typography variant="body2">
                  Còn Lại (Đã lọc)
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Biểu đồ 3 ngày gần nhất */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6">
              📊 Chi Tiêu 3 Ngày Gần Nhất
            </Typography>
            <Tooltip title={showAmounts ? 'Ẩn số tiền' : 'Hiển số tiền'} arrow>
              <IconButton size="small" onClick={() => setShowAmounts(!showAmounts)}>
                {showAmounts ? '👁️' : '🚫'}
              </IconButton>
            </Tooltip>
          </Box>
          {(() => {
            const data = getLast3DaysData();
            const max = Math.max(1, ...data.map(d => d.expense));
            const barWidth = 200;
            const barHeight = 30;
            const gap = 15;
            const height = data.length * (barHeight + gap) + 20;
            const labelWidth = 200; // Increased for longer labels
            const amountWidth = 100; // Space for amounts
            return (
              <Box sx={{ overflowX: 'auto' }}>
                <svg width={labelWidth + barWidth + amountWidth} height={height} role="img" aria-label="Last 3 days chart">
                  {data.map((d, i) => {
                    const w = Math.round((d.expense / max) * barWidth);
                    const isLastMonth = i === data.length - 1;
                    return (
                      <g key={d.date} transform={`translate(0, ${i * (barHeight + gap)})`}>
                        <text x={0} y={barHeight - 8} fontSize={13} fill="#333" fontWeight={600}>{d.label}</text>
                        <rect x={180} y={0} width={barWidth} height={barHeight} fill="#f5f5f5" rx={6} />
                        <rect x={180} y={0} width={w} height={barHeight} fill={isLastMonth ? '#9e9e9e' : '#f44336'} rx={6} />
                        <text x={180 + barWidth + 10} y={barHeight - 8} fontSize={13} fill="#111" fontWeight={600}>
                          {!showAmounts ? '***' : (shouldHideAmount(d.expense) ? '***' : `${formatMoneyShort(d.expense)}`)}
                        </text>
                      </g>
                    );
                  })}
                </svg>
              </Box>
            );
          })()}
        </CardContent>
      </Card>


      {/* Biểu đồ Category */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">
              Top 5 Chi Tiêu
            </Typography>
            {excludeKeywords.length > 0 && (
              <Button
                size="small"
                onClick={() => {
                  setExcludeKeywords([]);
                  localStorage.removeItem('excludeKeywords');
                  setSnackbarMessage('Đã reset danh sách loại trừ');
                  setSnackbarSeverity('success');
                  setSnackbarOpen(true);
                }}
                sx={{ fontSize: 11 }}
              >
                Reset Filter
              </Button>
            )}
          </Box>

          {/* Show excluded keywords */}
          {excludeKeywords.length > 0 && (
            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              <Typography variant="caption" sx={{ mr: 1, alignSelf: 'center', color: 'text.secondary' }}>
                Đã loại trừ:
              </Typography>
              {excludeKeywords.map((keyword, idx) => (
                <Chip
                  key={idx}
                  label={keyword}
                  size="small"
                  onDelete={() => {
                    const newList = excludeKeywords.filter((_, i) => i !== idx);
                    setExcludeKeywords(newList);
                    localStorage.setItem('excludeKeywords', JSON.stringify(newList));
                  }}
                  sx={{ fontSize: 10 }}
                />
              ))}
            </Box>
          )}

          {(() => {
            const data = getCategoryAnalysis();
            if (data.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  Không có dữ liệu chi tiêu
                </Typography>
              );
            }
            const max = Math.max(1, ...data.map(d => d.amount));
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {data.map((d, i) => {
                  const percentage = (d.amount / max) * 100;
                  return (
                    <Box key={d.category} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ minWidth: 20, color: 'text.secondary', fontWeight: 600 }}>
                        {i + 1}.
                      </Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }}>
                            {d.category}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13, ml: 1 }}>
                            {!showAmounts ? '***' : (shouldHideAmount(d.amount) ? '***' : `${formatMoneyShort(d.amount)}đ`)}
                          </Typography>
                        </Box>
                        <Box sx={{
                          width: '100%',
                          height: 8,
                          bgcolor: '#f5f5f5',
                          borderRadius: 1,
                          overflow: 'hidden'
                        }}>
                          <Box sx={{
                            width: `${percentage}%`,
                            height: '100%',
                            bgcolor: `hsl(${i * 60}, 70%, 50%)`,
                            transition: 'width 0.3s ease'
                          }} />
                        </Box>
                      </Box>
                      <Tooltip title="Loại trừ category này" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleExcludeCategory(d.category)}
                          sx={{
                            ml: 1,
                            width: 24,
                            height: 24,
                            '&:hover': {
                              bgcolor: 'error.light',
                              color: 'white'
                            }
                          }}
                        >
                          <Close sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Box>
            );
          })()}
        </CardContent>
      </Card>


      {/* Báo cáo tổng quan 3 tháng (có thể xổ xuống) */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }} onClick={() => setShowMonthlyReports(!showMonthlyReports)}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              📈 Báo cáo tổng quan 3 tháng
            </Typography>
            <IconButton size="small">
              {showMonthlyReports ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={showMonthlyReports}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
              {getLast3MonthsData().map((monthData, index) => {
                const balance = monthData.income - monthData.expense;
                const isCurrentMonth = index === getLast3MonthsData().length - 1;
                const allData = getLast3MonthsData();

                // Calculate trends compared to previous month
                const prevMonth = index > 0 ? allData[index - 1] : null;
                const incomeTrend = prevMonth ? (monthData.income > prevMonth.income ? 'up' : monthData.income < prevMonth.income ? 'down' : 'flat') : 'flat';
                const expenseTrend = prevMonth ? (monthData.expense > prevMonth.expense ? 'up' : monthData.expense < prevMonth.expense ? 'down' : 'flat') : 'flat';
                const balanceTrend = prevMonth ? ((monthData.income - monthData.expense) > (prevMonth.income - prevMonth.expense) ? 'up' : (monthData.income - monthData.expense) < (prevMonth.income - prevMonth.expense) ? 'down' : 'flat') : 'flat';

                const getTrendIcon = (trend: string, isExpense = false) => {
                  if (trend === 'up') return isExpense ? <TrendingUp sx={{ fontSize: 16, color: '#f44336' }} /> : <TrendingUp sx={{ fontSize: 16, color: '#4caf50' }} />;
                  if (trend === 'down') return isExpense ? <TrendingDown sx={{ fontSize: 16, color: '#4caf50' }} /> : <TrendingDown sx={{ fontSize: 16, color: '#f44336' }} />;
                  return <TrendingFlat sx={{ fontSize: 16, color: '#9e9e9e' }} />;
                };

                return (
                  <Card key={`${monthData.year}-${monthData.month}`}
                    sx={{
                      flex: 1,
                      minWidth: 220,
                      border: isCurrentMonth ? '2px solid #1976d2' : '1px solid #e0e0e0',
                      boxShadow: isCurrentMonth ? '0 4px 12px rgba(25, 118, 210, 0.2)' : '0 2px 8px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease'
                    }}>
                    <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                      <Typography variant="h6" fontWeight="bold" color={isCurrentMonth ? 'primary.main' : 'text.primary'}>
                        Tháng {monthData.label}
                        {isCurrentMonth && (
                          <Chip label="Hiện tại" size="small" color="primary" sx={{ ml: 1 }} />
                        )}
                      </Typography>

                      <Box sx={{ mt: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" color="success.main" fontWeight="medium">Thu:</Typography>
                            {getTrendIcon(incomeTrend)}
                          </Box>
                          <Typography variant="body1" fontWeight="bold" color="success.main">
                            +{formatMoney(monthData.income)}đ
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" color="error.main" fontWeight="medium">Chi:</Typography>
                            {getTrendIcon(expenseTrend, true)}
                          </Box>
                          <Typography variant="body1" fontWeight="bold" color="error.main">
                            -{formatMoney(monthData.expense)}đ
                          </Typography>
                        </Box>

                        <Divider sx={{ my: 1 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" fontWeight="medium">Còn lại:</Typography>
                            {getTrendIcon(balanceTrend)}
                          </Box>
                          <Typography variant="h6" fontWeight="bold"
                            color={balance >= 0 ? 'success.main' : 'error.main'}>
                            {balance >= 0 ? '+' : ''}{formatMoney(balance)}đ
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* Biểu đồ 3 tháng gần nhất */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6">
              Biểu Đồ Xu Hướng 3 Tháng
            </Typography>
            <Tooltip title={showAmounts ? 'Ẩn số tiền' : 'Hiển số tiền'} arrow>
              <IconButton size="small" onClick={() => setShowAmounts(!showAmounts)}>
                {showAmounts ? '👁️' : '🚫'}
              </IconButton>
            </Tooltip>
          </Box>
          {(() => {
            const data = getLast3MonthsData();
            const max = Math.max(1, ...data.flatMap(d => [d.income, d.expense]));
            const chartW = 600;
            const chartH = 350;
            const leftPad = 100;
            const rightPad = 80;
            const bottomPad = 100;
            const topPad = 60;
            const plotWidth = chartW - leftPad - rightPad;
            const plotHeight = chartH - bottomPad - topPad;

            // Calculate points for lines
            const getX = (index: number) => leftPad + (index * plotWidth) / (data.length - 1);
            const getY = (value: number) => chartH - bottomPad - (value / max) * plotHeight;

            // Generate path strings for lines
            const incomePath = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(d.income)}`).join(' ');
            const expensePath = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(d.expense)}`).join(' ');

            return (
              <Box sx={{ overflowX: 'auto' }}>
                <svg width={chartW} height={chartH} role="img" aria-label="3-months line chart">
                  {/* Grid lines */}
                  {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
                    const y = chartH - bottomPad - (ratio * plotHeight);
                    return (
                      <line key={idx} x1={leftPad} y1={y} x2={chartW - rightPad} y2={y}
                        stroke="#f0f0f0" strokeWidth="1" strokeDasharray="2,2" />
                    );
                  })}

                  {/* Axes */}
                  <line x1={leftPad} y1={chartH - bottomPad} x2={chartW - rightPad} y2={chartH - bottomPad}
                    stroke="#333" strokeWidth="2" />
                  <line x1={leftPad} y1={topPad} x2={leftPad} y2={chartH - bottomPad}
                    stroke="#333" strokeWidth="2" />

                  {/* Income line */}
                  <path d={incomePath} stroke="#4caf50" strokeWidth="3" fill="none"
                    strokeLinecap="round" strokeLinejoin="round" />

                  {/* Expense line */}
                  <path d={expensePath} stroke="#f44336" strokeWidth="3" fill="none"
                    strokeLinecap="round" strokeLinejoin="round" />

                  {/* Data points and labels */}
                  {data.map((d, i) => {
                    const x = getX(i);
                    const incomeY = getY(d.income);
                    const expenseY = getY(d.expense);
                    const balance = d.income - d.expense;

                    return (
                      <g key={`${d.year}-${d.month}`}>
                        {/* Income point */}
                        <circle cx={x} cy={incomeY} r="5" fill="#4caf50" stroke="white" strokeWidth="2" />
                        {/* Expense point */}
                        <circle cx={x} cy={expenseY} r="5" fill="#f44336" stroke="white" strokeWidth="2" />

                        {/* Month label */}
                        <text x={x} y={chartH - bottomPad + 20} fontSize={12} textAnchor="middle" fill="#333" fontWeight="bold">
                          {d.label}
                        </text>

                        {/* Income amount label */}
                        <text x={x} y={incomeY - 18} fontSize={11} textAnchor="middle" fill="#2e7d32" fontWeight="bold">
                          {!showAmounts ? '***' : formatMoneyShort(d.income)}
                        </text>

                        {/* Expense amount label */}
                        <text x={x} y={expenseY + 25} fontSize={11} textAnchor="middle" fill="#c62828" fontWeight="bold">
                          {!showAmounts ? '***' : formatMoneyShort(d.expense)}
                        </text>

                        {/* Balance label */}
                        <text x={x} y={chartH - bottomPad + 45} fontSize={12} textAnchor="middle"
                          fill={balance >= 0 ? "#2e7d32" : "#c62828"} fontWeight="bold">
                          {!showAmounts ? '***' : `Còn lại: ${balance >= 0 ? '+' : ''}${formatMoneyShort(balance)}`}
                        </text>
                      </g>
                    );
                  })}

                  {/* Legend */}
                  <g>
                    <circle cx={leftPad + 10} cy={20} r="5" fill="#4caf50" />
                    <text x={leftPad + 20} y={25} fontSize={12} fill="#555" fontWeight="bold">Thu nhập</text>
                    <circle cx={leftPad + 90} cy={20} r="5" fill="#f44336" />
                    <text x={leftPad + 100} y={25} fontSize={12} fill="#555" fontWeight="bold">Chi tiêu</text>
                  </g>

                  {/* Y-axis scale labels */}
                  {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
                    const y = chartH - bottomPad - (ratio * plotHeight);
                    const value = Math.round(max * ratio);
                    return (
                      <text key={idx} x={leftPad - 10} y={y + 4} fontSize={10} textAnchor="end" fill="#666">
                        {!showAmounts ? '***' : formatMoneyShort(value)}
                      </text>
                    );
                  })}
                </svg>
              </Box>
            );
          })()}
        </CardContent>
      </Card>

      {/* Search and Filter */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Search />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>🔍 Bộ lọc khác</Typography>
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              {showFilters ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

          <Collapse in={showFilters}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Tìm kiếm"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                fullWidth
                size="small"
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Từ ngày"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Đến ngày"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  size="small"
                  sx={{ flex: 1 }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Danh mục</InputLabel>
                  <Select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    label="Danh mục"
                  >
                    <MenuItem value="">Tất cả</MenuItem>
                    {Array.from(new Set(transactions.map(t => t.category))).map(category => (
                      <MenuItem key={category} value={category}>{category}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Loại</InputLabel>
                  <Select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    label="Loại"
                  >
                    <MenuItem value="">Tất cả</MenuItem>
                    <MenuItem value="income">Thu</MenuItem>
                    <MenuItem value="expense">Chi</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="outlined" onClick={clearFilters} size="small">
                  Xóa bộ lọc
                </Button>
                <Button variant="contained" onClick={applyFilters} size="small">
                  Áp dụng
                </Button>
              </Box>
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* Transactions List */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Danh Sách Giao Dịch ({filteredTransactions.length})
          </Typography>

          {displayedTransactions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
              {filteredTransactions.length === 0 ? 'Chưa có giao dịch nào' : 'Không tìm thấy giao dịch phù hợp'}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {displayedTransactions.map((transaction, index) => (
                <Box key={index}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight="medium">{transaction.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(transaction.date).toLocaleDateString('vi-VN')}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ textAlign: 'right' }}>
                        {renderAmount(transaction.amount, transaction.type)}
                        <Chip label={transaction.category} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Tooltip title="Chỉnh sửa giao dịch" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleEditClick(transaction)}
                            sx={{
                              p: 0.5,
                              bgcolor: 'primary.main',
                              color: 'white',
                              '&:hover': {
                                bgcolor: 'primary.dark',
                                transform: 'scale(1.1)',
                                transition: 'all 0.2s ease-in-out'
                              }
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Xóa giao dịch" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(transaction)}
                            sx={{
                              p: 0.5,
                              bgcolor: 'error.main',
                              color: 'white',
                              '&:hover': {
                                bgcolor: 'error.dark',
                                transform: 'scale(1.1)',
                                transition: 'all 0.2s ease-in-out'
                              }
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                  {index < displayedTransactions.length - 1 && <Divider sx={{ mt: 1 }} />}
                </Box>
              ))}
            </Box>
          )}

          {canLoadMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button variant="outlined" onClick={loadMore} startIcon={<ExpandMore />}>
                Xem thêm ({filteredTransactions.length - displayedTransactions.length})
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !isEditing && setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Slide}
        TransitionProps={{ direction: "up" } as any}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Edit />
          Chỉnh Sửa Giao Dịch
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="Ngày"
              type="date"
              value={editFormData.date}
              onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
              fullWidth
              variant="outlined"
              disabled={isEditing}
            />
            <TextField
              label="Tiêu đề"
              value={editFormData.title}
              onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
              fullWidth
              variant="outlined"
              disabled={isEditing}
            />
            <TextField
              label="Danh mục"
              value={editFormData.category}
              onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
              fullWidth
              variant="outlined"
              disabled={isEditing}
            />
            <FormControl fullWidth variant="outlined" disabled={isEditing}>
              <InputLabel>Loại</InputLabel>
              <Select
                value={editFormData.type}
                onChange={(e) => setEditFormData({ ...editFormData, type: e.target.value as 'income' | 'expense' })}
                label="Loại"
              >
                <MenuItem value="income">Thu</MenuItem>
                <MenuItem value="expense">Chi</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Số tiền"
              type="number"
              value={editFormData.amount}
              onChange={(e) => setEditFormData({ ...editFormData, amount: parseFloat(e.target.value) || 0 })}
              fullWidth
              variant="outlined"
              disabled={isEditing}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            disabled={isEditing}
            startIcon={<Cancel />}
            sx={{ minWidth: 100 }}
          >
            Hủy
          </Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            disabled={isEditing}
            startIcon={isEditing ? <CircularProgress size={16} color="inherit" /> : <Save />}
            sx={{ minWidth: 100 }}
          >
            {isEditing ? 'Đang lưu...' : 'Lưu'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isDeleting && setDeleteDialogOpen(false)}
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{
          bgcolor: 'error.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Delete />
          Xác Nhận Xóa
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'error.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1
            }}>
              <Delete sx={{ fontSize: 40, color: 'error.main' }} />
            </Box>
            <Typography variant="h6" align="center" gutterBottom>
              Bạn có chắc chắn muốn xóa giao dịch này?
            </Typography>
            <Box sx={{
              bgcolor: 'grey.100',
              p: 2,
              borderRadius: 2,
              width: '100%',
              border: '1px solid',
              borderColor: 'grey.300'
            }}>
              <Typography variant="body1" fontWeight="medium">
                {selectedTransaction?.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedTransaction?.date && new Date(selectedTransaction.date).toLocaleDateString('vi-VN')}
              </Typography>
              <Typography variant="body2" color={selectedTransaction?.type === 'income' ? 'success.main' : 'error.main'}>
                {selectedTransaction?.type === 'income' ? '+' : '-'}{selectedTransaction?.amount?.toLocaleString('vi-VN')}đ
              </Typography>
            </Box>
            <Typography variant="body2" color="error.main" align="center" sx={{ fontWeight: 'medium' }}>
              ⚠️ Hành động này không thể hoàn tác
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={isDeleting}
            startIcon={<Cancel />}
            sx={{ minWidth: 100 }}
          >
            Hủy
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
            sx={{ minWidth: 100 }}
          >
            {isDeleting ? 'Đang xóa...' : 'Xóa'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* AI Assistant Button - Fixed */}
      <Tooltip title="Trợ lý AI" arrow placement="left">
        <IconButton
          onClick={() => setShowAIAssistant(true)}
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 20,
            bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            width: 60,
            height: 60,
            boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              transform: 'scale(1.1)',
              boxShadow: '0 6px 25px rgba(102, 126, 234, 0.6)',
            },
            transition: 'all 0.3s ease',
            zIndex: 1300,
          }}
        >
          <SmartToy fontSize="large" />
        </IconButton>
      </Tooltip>

      {/* AI Assistant Chat Interface - Slide Panel */}
      <Slide direction="left" in={showAIAssistant} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: { xs: '100%', sm: '450px', md: '500px' },
            height: '100vh',
            bgcolor: 'background.paper',
            boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
            zIndex: 1400,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              p: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            }}
          >
            <SmartToy sx={{ mr: 1.5, fontSize: 28 }} />
            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
              Trợ lý AI
            </Typography>
            <IconButton
              onClick={() => setShowAIAssistant(false)}
              sx={{
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              <Close />
            </IconButton>
          </Box>
          <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
            <ChatInterface tenantConfig={tenantConfig} transactions={transactions} />
          </Box>
        </Box>
      </Slide>
    </Box>
  );
};

export default SimpleReport; 